import { useMutation } from "@apollo/client/react";
import type { FeatureCollection } from "geojson";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  CreateStandaloneGeoItemDocument,
  type CreateStandaloneGeoItemMutation,
  type CreateStandaloneGeoItemMutationVariables,
} from "~/features/geoItems/gqls";
import type { GeoItemCreateFormData } from "~/features/geoItems/types";
import { isCreateGeoItemGeometry } from "~/features/geoItems/utils/isCreateGeoItemGeometry";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";
import { resolveGdtfJdnRange } from "~/utils/gdtfRange";

interface UseGeoItemCreateProps {
  onCreated: (geoItemId: string) => void;
}

type FeatureCollectionAreaInput = {
  kind: "feature_collection";
  featureCollection: FeatureCollection;
};

type GeoItemCreateMutationVariables = Omit<CreateStandaloneGeoItemMutationVariables, "input"> & {
  input: Omit<CreateStandaloneGeoItemMutationVariables["input"], "areaInput"> & {
    areaInput: CreateStandaloneGeoItemMutationVariables["input"]["areaInput"] | FeatureCollectionAreaInput;
  };
};

/** Create a geo item with no record association (`cnutCreateGeoItemWithoutRecord`). */
export function useGeoItemCreate({ onCreated }: UseGeoItemCreateProps) {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [processing, setProcessing] = useState(false);
  const [createGeoItem] = useMutation<CreateStandaloneGeoItemMutation, GeoItemCreateMutationVariables>(
    CreateStandaloneGeoItemDocument,
  );

  const submit = useCallback(
    async (form: GeoItemCreateFormData) => {
      if (!tenantId) {
        toast.error(t("error.unknown"));
        return false;
      }
      // The map accepts shapes the create mutation cannot store; reject them before submitting.
      if (form.geometry && !isCreateGeoItemGeometry(form.geometry)) {
        toast.error(t("feature.recordGeoItems.form.errors.unsupportedGeoJsonGeometry"));
        return false;
      }

      setProcessing(true);
      try {
        const { fromJdn, toJdn } = resolveGdtfJdnRange(form.startDate || undefined, form.endDate || undefined);
        const labels = [
          form.jaLabel ? { lang: "ja", value: form.jaLabel, preferred: true } : null,
          form.enLabel ? { lang: "en", value: form.enLabel, preferred: false } : null,
        ].filter((label) => label !== null);

        const res = await createGeoItem({
          variables: {
            input: {
              tenantId,
              fromJdn,
              toJdn,
              areaInput: form.geometry
                ? form.geometry.type === "FeatureCollection"
                  ? { kind: "feature_collection", featureCollection: form.geometry }
                  : { kind: "geometry", geometry: form.geometry }
                : { kind: "h3", h3Indexes: form.h3Indexes },
              labels,
              metadata: form.memo ? { memo: form.memo } : undefined,
            },
          },
        });

        toast.success(t("feature.common.createdSuccessfully", { model: "geoItem" }));
        onCreated(res.data?.cnutCreateGeoItemWithoutRecord?.id ?? "");
        return true;
      } catch (e) {
        toast.error(translateError(t, e));
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [createGeoItem, onCreated, t, tenantId],
  );

  return { submit, processing };
}
